import * as React from "react"
import { auctionParamsSelector } from "../../../recoil/atoms/auction"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { Flex, Box, Text, Button, Input } from "@chakra-ui/react"
import { brandingSelector } from "../../../recoil/atoms/room"
import { ErrorTextContainer } from "../../common/fields/fields.styled"
import { SDKContext } from "../../../context/SDKContext"
import * as Yup from "yup"
import { blendWithWhiteBackground, numberWithCommas, numberWithoutCommas } from "../../../util"
import { AuctionOverlayBidProps } from "./auction.interface"
import { Icon } from "../../common/icons"
import { IoClose } from "react-icons/io5"
import { useTranslation } from "react-i18next"

/* const AuctionStatus = window.MeetOnline.AuctionStatus */
const AuctionIncrementType = window.MeetOnline.AuctionIncrementType

const _AuctionOverlayBid: React.FC<AuctionOverlayBidProps> = ({
  isMobile,
  isLandscape
}): JSX.Element => {
  const roomStyles = useRecoilValue(brandingSelector)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const setAuctionParams = useSetRecoilState(auctionParamsSelector)
  const {
    currency,
    id,
    ask,
    incrementType,
    fixedIncrementValue,
    percentIncrementValue
  } = useRecoilValue(auctionParamsSelector)
  const meetingClient = React.useContext(SDKContext)

  const closeModalBid = React.useCallback(() => {
    setAuctionParams({ isModalBid: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const { t } = useTranslation()
  const [text, setText] = React.useState("")

  const widthInput = React.useMemo(() => {
    if (text.length < 4 && !isMobile) {
      return "65px"
    }
    if (isMobile && text.length < 7) {
      return "65px"
    }
    return `calc(65px + ${
      (text.length - (isMobile ? 7 : 3)) * (isMobile ? 10 : 18)
    }px)`
  }, [isMobile, text.length])

  const [isError, setError] = React.useState("")

  const schema = Yup.string()
    .test(
      "MustBeGreaterThanOrEqualTo",
      t("Must be greater than or equal to"),
      function () {
        return numberWithoutCommas(text) >= ask
      }
    )
    .max(1024, "MaxRTMP")
    .nullable()

  const handlePlaceBid = React.useCallback(async () => {
    try {
      if (text.length === 0) {
        if (incrementType === AuctionIncrementType.MANUAL) {
          meetingClient?.placeBit(id, ask)
          meetingClient?.placeAsk(id, 0)
        }

        if (incrementType === AuctionIncrementType.AUTO_FIXED) {
          const autoFixedValue = +ask + +fixedIncrementValue
          meetingClient?.placeBit(id, ask)
          meetingClient?.placeAsk(id, autoFixedValue)
        }

        if (incrementType === AuctionIncrementType.AUTO_PERCENT) {
          const autoPercentIncrementValue = (
            (+ask * +percentIncrementValue) / 100 +
            +ask
          ).toFixed(2)

          meetingClient?.placeBit(id, ask)
          meetingClient?.placeAsk(id, autoPercentIncrementValue)
        }
      } else {
        const valid = await schema.validate(numberWithoutCommas(text))
        if (incrementType === AuctionIncrementType.MANUAL) {
          meetingClient?.placeBit(id, valid)
          meetingClient?.placeAsk(id, 0)
        }

        if (incrementType === AuctionIncrementType.AUTO_FIXED) {
          const autoFixedValue =
            +numberWithoutCommas(text) + +fixedIncrementValue
          meetingClient?.placeBit(id, valid)
          meetingClient?.placeAsk(id, autoFixedValue)
        }

        if (incrementType === AuctionIncrementType.AUTO_PERCENT) {
          const autoPercentIncrementValue = (
            (+numberWithoutCommas(text) * +percentIncrementValue) / 100 +
            +numberWithoutCommas(text)
          ).toFixed(2)
          meetingClient?.placeBit(id, valid)
          meetingClient?.placeAsk(id, autoPercentIncrementValue)
        }
      }
      closeModalBid()
    } catch (e) {
      setError(
        `${t(
          "Must be equal to or greater than"
        )} ${currency} ${numberWithCommas(ask)}`
      )
    }
  }, [
    ask,
    closeModalBid,
    currency,
    fixedIncrementValue,
    id,
    incrementType,
    meetingClient,
    percentIncrementValue,
    schema,
    t,
    text
  ])

  return (
    <Box
      width="100vw"
      height="100vh"
      position="fixed"
      background="white"
      zIndex="10000000"
      top="0"
      left="0"
      backgroundColor="rgba(255, 255, 255, 0.5)"
    >
      <Flex
        minW={isMobile ? "300px" : "550px"}
        position="absolute"
        top={isLandscape ? "40%" : "50%"}
        left="50%"
        padding="1rem"
        marginRight="-50%"
        transform="translate(-50%, -50%)"
        flexDirection="column"
        backgroundColor="rgba(255, 255, 255)"
        borderRadius="0.375rem"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={roomStyles.servicePanelElementBorder ?? "gray.300"}
        bg={
          blendWithWhiteBackground(
            roomStyles?.servicePanelElementModalBackground
          ) ?? "white"
        }
        boxShadow={`0 0 15px ${
          roomStyles?.servicePanelElementBorder ?? "#e2e8f0"
        }`}
        color={roomStyles?.servicePanelElementPrimaryText ?? "black"}
      >
        {ask === 0 ? (
          <>
            <Icon
              onClick={closeModalBid}
              src={IoClose}
              position="absolute"
              right="0.1rem"
              top="0.1rem"
              cursor={"pointer"}
              opacity={0.75}
              zIndex="10000006"
              _hover={{ opacity: 1 }}
            />
            {isMobile ? (
              <>
                <Box
                  fontSize="1.5rem"
                  lineHeight="1.5"
                  fontWeight="700"
                  justifyContent="center"
                  zIndex="10000001"
                  textAlign="center"
                >
                  {t(`Your were beaten to it.`)}
                </Box>
                <Box
                  fontSize="1.5rem"
                  lineHeight="1.5"
                  fontWeight="700"
                  justifyContent="center"
                  zIndex="10000001"
                  textAlign="center"
                >
                  {t(`Please bid again`)}
                </Box>{" "}
              </>
            ) : (
              <Box
                fontSize="1.5rem"
                lineHeight="1.5"
                fontWeight="700"
                justifyContent="center"
                zIndex="10000001"
                textAlign="center"
              >{`${t(`Your were beaten to it.`)} ${t(
                `Please bid again`
              )}`}</Box>
            )}
          </>
        ) : (
          <>
            <Box
              fontSize="1.5rem"
              lineHeight="1.5"
              fontWeight="700"
              justifyContent="center"
              zIndex="10000001"
              textAlign="center"
              marginBottom="0.5rem"
            >
              {t("Your bid")}
            </Box>
            <ErrorTextContainer
              roomStyles={roomStyles}
              style={{
                top: "48px",
                left: "50%",
                transform: "translateX(-50%)",
                right: "unset",
                fontSize: "0.8rem",
                width: "500px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              {isError}
            </ErrorTextContainer>
            <Flex
              width="100%"
              alignItems="center"
              justifyContent="center"
              lineHeight={1.5}
            >
              <Text fontSize={isMobile ? "1rem" : "2rem"} fontWeight={"bold"}>
                {currency} {numberWithCommas((+ask).toFixed(2))}
              </Text>{" "}
              <Text
                fontSize={isMobile ? "1rem" : "2rem"}
                margin={"0rem 0.7rem 0rem 0.7rem"}
              >
                {t("or")}
              </Text>
              <Text fontSize={isMobile ? "1rem" : "2rem"} fontWeight={"bold"}>
                {currency}
              </Text>{" "}
              <Flex position="relative">
                {/*  {isError && (
        <ErrorTextContainer room={room} style={{top: "-35px"}}>{isError}</ErrorTextContainer>
      )} */}
                {isMobile ? (
                  <Input
                    variant="flushed"
                    type="text"
                    pattern="\d*"
                    maxLength={12}
                    max={12}
                    placeholder=""
                    height="30px"
                    fontWeight={"bold"}
                    fontSize={isMobile ? "1rem" : "2rem"}
                    marginLeft="7px"
                    autoFocus
                    marginTop="auto"
                    width={widthInput}
                    value={text}
                    /* onBlur={() => setIsTouch(false)} */
                    name={"name"}
                    onChange={e => {
                      const re = /[0-9]|\./

                      if (e.target.value === "" || re.test(e.target.value)) {
                        const withoutCommas = numberWithoutCommas(
                          e.target.value
                        )
                        const value = numberWithCommas(withoutCommas)
                        setText(value)
                      }
                    }}
                    borderBottom="2px"
                    borderColor={`${roomStyles?.servicePanelElementPrimaryText}` ?? "inherit"}
                    color={
                      roomStyles?.servicePanelElementPrimaryText ?? "black"
                    }
                    _hover={{
                      borderColor: roomStyles?.servicePanelElementPrimaryText ?? "gray.300"
                    }}
                    _focus={{
                      borderColor: roomStyles?.servicePanelElementPrimaryText ?? "gray.500"
                    }}
                  />
                ) : (
                  <Input
                    variant="flushed"
                    type="text"
                    maxLength={12}
                    max={12}
                    placeholder=""
                    height="30px"
                    fontWeight={"bold"}
                    fontSize={isMobile ? "1rem" : "2rem"}
                    marginLeft="7px"
                    autoFocus
                    marginTop="auto"
                    width={widthInput}
                    value={text}
                    /* onBlur={() => setIsTouch(false)} */
                    name={"name"}
                    onChange={e => {
                      const re = /[0-9]|\./

                      if (e.target.value === "" || re.test(e.target.value)) {
                        const withoutCommas = numberWithoutCommas(
                          e.target.value
                        )
                        const value = numberWithCommas(withoutCommas)
                        setText(value)
                      }
                    }}
                    borderBottom="2px"
                    borderColor={`${roomStyles?.servicePanelElementPrimaryText}` ?? "inherit"}
                    color={
                      roomStyles?.servicePanelElementPrimaryText ?? "black"
                    }
                    _hover={{
                      borderColor: roomStyles?.servicePanelElementPrimaryText ?? "gray.300"
                    }}
                    _focus={{
                      borderColor: roomStyles?.servicePanelElementPrimaryText ?? "gray.500"
                    }}
                    padding={isSafari && "3px 0px"}
                    lineHeight={isSafari && 1}
                    boxSizing={isSafari ? "content-box" : "border-box"}
                  />
                )}
              </Flex>
            </Flex>
            <Flex
              alignItems="center"
              justifyContent="center"
              marginTop="0.4rem"
            >
              <Button
                onClick={closeModalBid}
                colorscheme="teal"
                variant="outline"
                width="unset"
                size={"sm"}
                fontSize={"sm"}
                style={{
                  marginRight: "5px",
                  alignSelf: "flex-end",
                  width: "unset"
                }}
                bg={
                  roomStyles?.secondaryAccessButtonBackgroundColor ?? "gray.100"
                }
                textColor={
                  roomStyles?.secondaryAccessButtonTextColor ?? "#718096"
                }
                _hover={{
                  backgroundColor:
                    roomStyles?.secondaryAccessButtonMouseoverColor ?? "#edf2f7",
                  textColor:
                    roomStyles?.secondaryAccessButtonTextMouseoverColor ?? "#000"
                }}
                borderColor={
                  roomStyles?.secondaryAccessButtonBorderColor ?? "#e2e8f0"
                }
                opacity={0.75}
                _focus={{}}
              >
                {t(`Cancel`)}
              </Button>
              <Button
                colorscheme="teal"
                onClick={handlePlaceBid}
                variant="outline"
                size={"sm"}
                fontSize={"sm"}
                style={{ alignSelf: "flex-end" }}
                _focus={{}}
                bg={
                  roomStyles?.secondaryAccessButtonBackgroundColor ?? "gray.100"
                }
                textColor={
                  roomStyles?.secondaryAccessButtonTextColor ?? "#718096"
                }
                _hover={{
                  backgroundColor:
                    roomStyles?.secondaryAccessButtonMouseoverColor ?? "#edf2f7",
                  textColor:
                    roomStyles?.secondaryAccessButtonTextMouseoverColor ?? "#000"
                }}
                borderColor={
                  roomStyles?.secondaryAccessButtonBorderColor ?? "#e2e8f0"
                }
              >
                {text.length === 0
                  ? `${t("Confirm")}  ${currency} ${numberWithCommas(
                      (+ask).toFixed(2)
                    )} bid`
                  : `${t("Confirm")} ${currency}  ${text} ${t("bid")}`}
              </Button>
            </Flex>
          </>
        )}
      </Flex>
    </Box>
  )
}

export const AuctionOverlayBid = React.memo(_AuctionOverlayBid)
